import TagManager from "react-gtm-module";

// GTM Container ID
const GTM_ID = process.env.REACT_APP_GTM_ID;

// Check if user has consented to analytics
export const hasAnalyticsConsent = (): boolean => {
  return localStorage.getItem("bhss-cookies") === "true";
};

// Initialize GTM (only call after user consent)
export const initializeGTM = (): void => {
  if (!GTM_ID) {
    return;
  }

  if (!hasAnalyticsConsent()) {
    return;
  }

  TagManager.initialize({
    gtmId: GTM_ID,
  });
};

// DataLayer variable types
export interface UserDataLayerVariables {
  user_segment?: "vip" | "standard" | "other";
  user_role?: "admin" | "user";
  user_id?: string;
  user_language?: "lt" | "en";
  is_internal_user?: boolean;
  company_id?: string;
  company_name?: string;
}

export interface DataLayerEvent {
  event: string;
  [key: string]: any;
}

// Module-level traffic_type — set once in initializeDataLayer, then
// automatically included in every pushToDataLayer call for internal users
let _trafficType: "internal" | undefined;

// Module-level user_segment — set once in initializeDataLayer, then
// automatically included in every pushToDataLayer call
let _userSegment: "vip" | "other" = "other";

// Initialize dataLayer with user variables
export const initializeDataLayer = (userData: UserDataLayerVariables): void => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    user_segment: userData.user_segment || "other",
    user_role: userData.user_role || "user",
    user_id: userData.user_id || "",
    user_language: userData.user_language || "lt",
    is_internal_user: userData.is_internal_user || false,
    company_id: userData.company_id || null,
    company_name: userData.company_name || null,
    ...(userData.is_internal_user ? { traffic_type: "internal" } : {}),
  });

  _trafficType = userData.is_internal_user ? "internal" : undefined;
  _userSegment = userData.user_segment === "vip" ? "vip" : "other";
};

// Push event to dataLayer
export const pushToDataLayer = (data: DataLayerEvent): void => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ...(_trafficType ? { traffic_type: _trafficType } : {}),
    user_segment: _userSegment,
    ...data,
  });
};

// Helper to map backend role to GTM user_role
export const mapUserRole = (backendRole: string): "admin" | "user" => {
  const adminRoles = ["root", "bhAdmin", "companyRootAdmin"];
  return adminRoles.includes(backendRole) ? "admin" : "user";
};

// Helper to determine if user is internal (BH staff)
export const isInternalUser = (backendRole: string): boolean => {
  return backendRole === "root" || backendRole === "bhAdmin";
};

// Event tracking helpers
export const trackLoginEvent = (
  loginMethod: "email" | "sso" | "magic_link",
  additionalData?: Record<string, any>
): void => {
  pushToDataLayer({
    event: "user_login",
    login_method: loginMethod,
    ...additionalData,
  });
};

export const trackModuleView = (moduleName: string): void => {
  pushToDataLayer({
    event: "module_view",
    module_name: moduleName,
  });
};

export const trackFormEvent = (
  eventName: "form_start" | "form_submit" | "form_error",
  formName: string,
  additionalData?: Record<string, any>
): void => {
  pushToDataLayer({
    event: eventName,
    form_name: formName,
    ...additionalData,
  });
};

export const trackIncidentEvent = (
  eventName: string,
  incidentId: string,
  additionalData?: Record<string, any>
): void => {
  pushToDataLayer({
    event: eventName,
    incident_id: incidentId,
    ...additionalData,
  });
};

export const trackDocumentEvent = (
  eventName: string,
  documentType: "invoice" | "contract" | "report" | "attachment",
  documentId: string,
  additionalData?: Record<string, any>
): void => {
  pushToDataLayer({
    event: eventName,
    document_type: documentType,
    document_id: documentId,
    ...additionalData,
  });
};

export const trackFileAttached = (
  fileType: "image" | "pdf" | "doc" | "xlsx" | "other" | string,
  additionalData?: Record<string, any>
): void => {
  pushToDataLayer({
    event: "file_attached",
    document_type: "attachment",
    file_type: fileType,
    ...additionalData,
  });
};

export const trackExportEvent = (
  eventName: string,
  exportFormat: "excel" | "pdf",
  moduleName: string,
  reportName?: string
): void => {
  pushToDataLayer({
    event: eventName,
    export_format: exportFormat,
    module_name: moduleName,
    ...(reportName && { export_report_name: reportName }),
  });
};

// GA4 Ecommerce: Invoice Payment Event
export const trackInvoicePayment = (paymentData: {
  transaction_id: string;
  value: number;
  currency?: string;
  tax?: number;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
  payment_method?: "card" | "bank_transfer" | "online_banking";
}): void => {
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: paymentData.transaction_id,
      value: paymentData.value,
      currency: paymentData.currency || "EUR",
      tax: paymentData.tax || 0,
      items: paymentData.items,
      ...(paymentData.payment_method && {
        payment_method: paymentData.payment_method,
      }),
    },
  });
};

export const trackMessageEvent = (
  messageCategory: "notification" | "announcement" | "system",
  additionalData?: Record<string, any>
): void => {
  pushToDataLayer({
    event: "message_view",
    message_category: messageCategory,
    ...additionalData,
  });
};

// Declare dataLayer on window object for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
  }
}
